import { useCallback, useEffect, useRef } from 'react';
import { useCursorMode } from '@/chrome/Cursor/useCursorMode';
import type { CursorController } from '@/chrome/Cursor/useCursorMode';
import { DIALOGUE_BY_LEVEL, type DialogueLevelKey } from '@/data/dialogue';
import { useDialogueStore } from '@/state/dialogueStore';
import { useGameStore } from '@/state/gameStore';
import type {
  DialoguePool,
  DialogueTrigger,
  FaceState,
  LevelId,
} from '@/state/types';
import { assertNever } from '@/utils/assert';

export interface LevelControllerOptions {
  id: LevelId;
  initialFace?: FaceState;
  onMount?: () => void;
  onUnmount?: () => void;
}

export interface LevelController {
  solve: (trigger?: DialogueTrigger) => void;
  queueAdvanceWithLine: (line: string) => void;
  loseToken: (reason: string) => void;
  setFace: (s: FaceState) => void;
  setDialogue: (trigger: DialogueTrigger) => void;
  recordComplimentSubmission: (text: string) => void;
  cursor: CursorController;
}

export function levelIdKey(id: LevelId): string {
  switch (id.kind) {
    case 'LEVEL':
      return `LEVEL:${String(id.n)}`;
    case 'FALSE_ENDING':
      return 'FALSE_ENDING';
    case 'ENDGAME':
      return 'ENDGAME';
    case 'GAME_OVER':
      return 'GAME_OVER';
    default:
      return assertNever(id);
  }
}

export function dialoguePoolFor(id: LevelId): DialoguePool | null {
  if (id.kind !== 'LEVEL') return null;
  const key = `L${String(id.n).padStart(2, '0')}` as DialogueLevelKey;
  const pool = (DIALOGUE_BY_LEVEL as Record<string, DialoguePool | undefined>)[key];
  return pool ?? null;
}

export function useLevelController(opts: LevelControllerOptions): LevelController {
  const cursor = useCursorMode();

  // Latest-callback refs so mount/unmount effect never restages on prop churn.
  const onMountRef = useRef(opts.onMount);
  const onUnmountRef = useRef(opts.onUnmount);
  const initialFaceRef = useRef(opts.initialFace);
  useEffect(() => {
    onMountRef.current = opts.onMount;
    onUnmountRef.current = opts.onUnmount;
    initialFaceRef.current = opts.initialFace;
  });

  const idKey = levelIdKey(opts.id);

  useEffect(() => {
    useGameStore.getState().setFaceState(initialFaceRef.current ?? 'DEFAULT');
    useDialogueStore.getState().setPool(dialoguePoolFor(opts.id));
    onMountRef.current?.();

    return () => {
      onUnmountRef.current?.();
      if (opts.id.kind !== 'GAME_OVER') {
        useDialogueStore.getState().clearLine();
      }
    };
    // idKey fully describes level identity; opts.id is not a stable ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idKey]);

  const solve = useCallback((trigger: DialogueTrigger = 'solution') => {
    // When a dialogue pool is loaded, queue a gated advance: fire the
    // requested trigger line (usually 'solution'), then wait for the player
    // to acknowledge via the DialogueBox arrow. Levels without a pool
    // (FALSE_ENDING, ENDGAME until built) advance immediately so nothing
    // gets stuck.
    const { currentPool, fire, setPendingAdvance } = useDialogueStore.getState();
    if (currentPool) {
      fire(trigger);
      setPendingAdvance(true);
      return;
    }
    useGameStore.getState().solveLevel();
  }, []);

  const queueAdvanceWithLine = useCallback((line: string) => {
    const { currentPool, setLine, setPendingAdvance } = useDialogueStore.getState();
    if (currentPool) {
      setLine(line);
      setPendingAdvance(true);
      return;
    }
    useGameStore.getState().solveLevel();
  }, []);

  const loseToken = useCallback((reason: string) => {
    useGameStore.getState().loseToken(reason);
    useDialogueStore.getState().fire('tokenLoss');
  }, []);

  const setFace = useCallback((s: FaceState) => {
    useGameStore.getState().setFaceState(s);
  }, []);

  const setDialogue = useCallback((trigger: DialogueTrigger) => {
    useDialogueStore.getState().fire(trigger);
  }, []);

  const recordComplimentSubmission = useCallback((text: string) => {
    useGameStore.getState().recordComplimentSubmission(text);
  }, []);

  return {
    solve,
    queueAdvanceWithLine,
    loseToken,
    setFace,
    setDialogue,
    recordComplimentSubmission,
    cursor,
  };
}
